import type { ReactNode } from 'react';

/** A `{ value, label, count }` row from a `/…/filter-options` endpoint. */
export interface Facet {
  value: string | number;
  label?: string | number | null;
  count?: number | null;
}

export interface FacetOption {
  value: string | number;
  label: string;
  /** Kept off `label` so the picker's search + selected chip never see it. */
  count?: number | null;
}

/**
 * Facet rows → Select options, with the count kept OUT of the label.
 *
 * Baking the count into the label (`"Pune (35)"`) makes it part of what the picker searches and of
 * what the selected chip shows — typing "35" would match Pune, and the chip would read "Pune (35)".
 * Here the label stays the bare value and the count rides alongside, rendered right-aligned by
 * `renderFacetOption` (pass it as the Select's `optionRender`).
 *
 * `label` remaps the display text (e.g. a state code → its name); rows whose value is blank are
 * dropped — an unlabelled option is unusable.
 */
export function facetOptions(
  rows: Facet[] | undefined | null,
  label?: (row: Facet) => string,
): FacetOption[] {
  return (rows ?? [])
    .filter((r) => String(r.value ?? '').trim() !== '')
    .map((r) => ({
      value: r.value,
      label: (label ? label(r) : String(r.label ?? r.value)).trim(),
      count: r.count,
    }));
}

/**
 * Select `optionRender` for facet options: value on the left, its count right-aligned and muted, so a
 * long list reads as two columns instead of a ragged "name (n)" run.
 */
export function renderFacetOption(o: any): ReactNode {
  return (
    <span className="flex w-full items-center justify-between gap-4">
      <span className="min-w-0">{o.label}</span>
      {o.count != null && <span className="shrink-0 text-xs tabular-nums text-subtle">{o.count}</span>}
    </span>
  );
}
